import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { addProduct, updateProduct, getProduct, uploadProductImage } from '../../actions/product';
import { Card, Form, Button, Container, Row, Col, Spinner, Alert } from 'react-bootstrap';

const initialState = {
    name: '',
    brand: '',
    tagline: '',
    description: '',
    image: '',
    productType: ''
};

const Product = ({
    addProduct,
    getProduct,
    updateProduct,
    loading,
    product,
    uploadProductImage
}) => {
    const { id } = useParams();
    const [formData, setFormData] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [imageUploading, setImageUploading] = useState(false);

    useEffect(() => {
        if (id !== 'new') {
            if (!product || (product._id !== id)) {
                getProduct(id);
            }
            const productData = { ...initialState };
            if (product) {
                for (const key in product) {
                    if (key in productData) productData[key] = product[key];
                }
                setFormData(productData);
            }
        }
    }, [product, id, getProduct]);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const {
        name,
        brand,
        tagline,
        description,
        productType,
        image,
    } = formData;

    const isNewProduct = (id === 'new');

    const onChange = async (e) => {
        if (e.target.name === 'image') {
            try {
                setImageUploading(true);
                setError('');
                const payload = {
                    'product_id': id,
                    'file': e.target.files[0]
                };
                await uploadProductImage(payload);
                setSuccess('Image uploaded successfully!');
            } catch (err) {
                setError('Failed to upload image. Please try again.');
            } finally {
                setImageUploading(false);
            }
            return;
        }
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError('');

            if (isNewProduct) {
                if (!formData.productType) {
                    setError('Please select a product type.');
                    return;
                }
                await addProduct(formData);
                setSuccess('Product added successfully!');
            } else {
                await updateProduct(product._id, formData);
                setSuccess('Product updated successfully!');
            }
        } catch (err) {
            setError(isNewProduct ? 'Failed to add product. Please try again.' : 'Failed to update product. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && !product) {
        return (
            <Container className="py-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading product details...</p>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <Fragment>
            <Container className="py-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                {/* Header */}
                <div className="mb-4">
                    <Link
                        to="/inventory/v1.2/products"
                        className="btn btn-outline-primary mb-3"
                    >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Products
                    </Link>
                    <div>
                        <h2 className="text-primary mb-1">
                            {isNewProduct ? 'Add New Product' : `Edit Product: ${name || 'Loading...'}`}
                        </h2>
                        <p className="text-muted mb-0">
                            {isNewProduct ? 'Create a new product in the inventory' : 'Update product information and settings'}
                        </p>
                    </div>
                </div>

                {/* Alert Messages */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        <i className="fas fa-check-circle me-2"></i>
                        {success}
                    </Alert>
                )}

                <Row className="justify-content-center">
                    <Col lg={8} xl={6}>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-box me-2"></i>
                                    Product Information
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Form onSubmit={onSubmit}>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Product Name *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter product name"
                                                    name="name"
                                                    value={name}
                                                    onChange={onChange}
                                                    required
                                                    disabled={isSubmitting}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Brand *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter brand name"
                                                    name="brand"
                                                    value={brand}
                                                    onChange={onChange}
                                                    required
                                                    disabled={isSubmitting}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Tagline</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter product tagline"
                                            name="tagline"
                                            value={tagline}
                                            onChange={onChange}
                                            disabled={isSubmitting}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            placeholder="Enter product description"
                                            name="description"
                                            value={description}
                                            onChange={onChange}
                                            disabled={isSubmitting}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Product Type *</Form.Label>
                                        <Form.Select
                                            name="productType"
                                            value={productType}
                                            onChange={onChange}
                                            required
                                            disabled={isSubmitting}
                                        >
                                            <option value="">Select Product Type</option>
                                            <option value="water">Water</option>
                                            <option value="membership">Membership</option>
                                            <option value="accessory">Accessory</option>
                                            <option value="refundableDeposit">Refundable Deposit</option>
                                        </Form.Select>
                                    </Form.Group>

                                    {!isNewProduct && (
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Image</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="image"
                                                onChange={onChange}
                                                accept="image/*"
                                                disabled={imageUploading || isSubmitting}
                                            />
                                            {imageUploading && (
                                                <div className="mt-2">
                                                    <Spinner size="sm" className="me-2" />
                                                    <small className="text-muted">Uploading image...</small>
                                                </div>
                                            )}
                                        </Form.Group>
                                    )}

                                    {!isNewProduct && image && (
                                        <div className="text-center mb-4">
                                            <Card className="d-inline-block">
                                                <Card.Body className="p-2">
                                                    <img
                                                        src={`${process.env.REACT_APP_SERVER_PUBLIC_PATH}${process.env.REACT_APP_IMAGE_PATH_PRODUCT}/${image}`}
                                                        alt="Product"
                                                        className="img-fluid rounded"
                                                        style={{ maxHeight: '200px', maxWidth: '200px' }}
                                                    />
                                                </Card.Body>
                                            </Card>
                                            <p className="text-muted mt-2 mb-0">Current Product Image</p>
                                        </div>
                                    )}

                                    <div className="d-grid">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            size="lg"
                                            disabled={isSubmitting || imageUploading}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    {isNewProduct ? 'Adding...' : 'Updating...'}
                                                </>
                                            ) : (
                                                <>
                                                    <i className={`fas ${isNewProduct ? 'fa-plus' : 'fa-edit'} me-2`}></i>
                                                    {isNewProduct ? 'Add Product' : 'Update Product'}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

Product.propTypes = {
    getProduct: PropTypes.func.isRequired,
    addProduct: PropTypes.func.isRequired,
    updateProduct: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    uploadProductImage: PropTypes.func.isRequired,
    product: PropTypes.object
};

const mapStateToProps = state => ({
    loading: state.product.loading,
    product: state.product.product
});

export default connect(
    mapStateToProps,
    { addProduct, getProduct, updateProduct, uploadProductImage }
)(Product);
